import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useContent } from "@/i18n";
import { firm } from "@/config/firm.config";
import { scrollToHash } from "@/hooks/useLenis";
import { EASE, MaskedLine } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1519674921880-e784bc34ebd4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";
const FRAME_IMG =
  "https://images.unsplash.com/photo-1523292864699-4dcfd824dae2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export const Hero = () => {
  const { t } = useContent();
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, reduce ? 0 : 180]);
  const bgScale = useTransform(scrollY, [0, 900], [1, reduce ? 1 : 1.12]);
  const frameY = useTransform(scrollY, [0, 900], [0, reduce ? 0 : -90]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  const go = (hash) => (e) => {
    e.preventDefault();
    scrollToHash(hash);
  };

  return (
    <section id="home" data-testid="hero-section" className="relative isolate min-h-[100svh] overflow-hidden bg-navy-900">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 -z-20 will-change-transform">
        <img src={HERO_IMG} alt="" aria-hidden="true" className="h-full w-full object-cover object-center opacity-[0.28]" />
      </motion.div>
      <div aria-hidden="true" className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,rgba(11,16,35,0.55)_0%,rgba(11,16,35,0.75)_55%,#0B1023_100%)]" />
      <div aria-hidden="true" className="gold-beam absolute -top-1/3 left-1/2 -z-10 h-[140%] w-[45vw] -translate-x-1/3 rotate-[18deg]" />

      <div className="relative mx-auto grid min-h-[100svh] max-w-[1440px] grid-cols-12 items-end gap-6 px-6 pb-20 pt-32 md:px-12 lg:items-center lg:pb-28">
        <div className="col-span-12 lg:col-span-7">
          <motion.div
            initial={reduce ? false : { scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.2, ease: EASE }}
            className="mb-8 h-px w-24 origin-left bg-gold"
          />
          <motion.p
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="eyebrow mb-6"
          >
            {t.hero.eyebrow}
          </motion.p>
          <h1 className="font-serif text-[15vw] font-light leading-[0.92] tracking-[-0.02em] text-paper sm:text-7xl lg:text-[6.5rem] xl:text-[7.5rem]">
            <MaskedLine delay={0.65}>{t.hero.lines[0]}</MaskedLine>
            <MaskedLine delay={0.8} className="italic text-gold-light">
              {t.hero.lines[1]}
            </MaskedLine>
          </h1>
          <motion.p
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="mt-10 max-w-xl text-base leading-relaxed text-ink sm:text-lg"
            data-testid="hero-subline"
          >
            {t.hero.subline}
          </motion.p>
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.4, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <a href="#contact" onClick={go("#contact")} data-testid="hero-contact-cta" className="btn-outline group">
              <span>{t.hero.cta}</span>
              <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink/70">{t.hero.badge}</span>
          </motion.div>
        </div>

        <motion.figure
          style={{ y: frameY }}
          initial={reduce ? false : { opacity: 0, clipPath: "inset(0 0 100% 0)" }}
          animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
          transition={{ duration: 1.4, delay: 0.9, ease: EASE }}
          className="relative col-span-12 hidden lg:col-span-4 lg:col-start-9 lg:block"
        >
          <div className="relative aspect-[3/4] overflow-hidden">
            <img src={FRAME_IMG} alt="Marble colonnade evoking a court corridor" className="h-full w-full object-cover" loading="eager" />
            <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(224,184,120,0.18),transparent_55%),linear-gradient(180deg,rgba(11,16,35,0.1),rgba(11,16,35,0.7))]" />
          </div>
          <span aria-hidden="true" className="absolute -left-3 -top-3 h-10 w-10 border-l border-t border-gold" />
          <span aria-hidden="true" className="absolute -bottom-3 -right-3 h-10 w-10 border-b border-r border-gold" />
          <figcaption className="mt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60">
            <span>{t.hero.frameCaption}</span>
            <span>{firm.address.pin}</span>
          </figcaption>
        </motion.figure>
      </div>

      <motion.div style={{ opacity: fade }} className="absolute bottom-8 left-6 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-ink/60 md:left-12">
        <ArrowDown size={12} className="animate-bob" />
        {t.hero.scroll}
      </motion.div>
    </section>
  );
};
